/**
 * @fileoverview SkeletonImage — image/thumbnail shimmer.
 * @package @puffin-core/skeleton-kit
 */

import { memo, useMemo } from 'react';
import PropTypes from 'prop-types';

/**
 * @typedef {object} SkeletonImageOwnProps
 * @property {string} [width] - A CSS length.
 * @property {string} [height] - A CSS length.
 * @property {string} [aspectRatio] - A CSS `aspect-ratio` value.
 * @property {boolean} [animated=true]
 * @property {string} [className]
 */

/**
 * The remaining props (an `aria-*` override, a test id) are spread onto the element.
 *
 * @typedef {SkeletonImageOwnProps & React.HTMLAttributes<HTMLDivElement>} SkeletonImageProps
 */

/**
 * Memoised; it keeps its `propTypes` declaration until JSDOC-PROP-CONTRACT-001 Phase 3 retires it.
 *
 * @type {React.NamedExoticComponent<SkeletonImageProps> & {propTypes?: object}}
 */
export const SkeletonImage = memo(
  /**
   * @param {SkeletonImageProps} props
   * @returns {React.JSX.Element}
   */
  function SkeletonImage({
    width,
    height,
    aspectRatio,
    animated = true,
    className = '',
    ...props
  }) {
    const style = useMemo(
      () => ({
        ...(width ? { width } : {}),
        ...(height ? { height } : {}),
        ...(aspectRatio ? { aspectRatio: aspectRatio.replace('/', ' / ') } : {}),
      }),
      [width, height, aspectRatio],
    );

    const classes = ['skeleton', 'skeleton-image', animated ? null : 'skeleton-static', className]
      .filter(Boolean)
      .join(' ')
      .trim();

    return (
      <div
        className={classes}
        aria-hidden="true"
        style={Object.keys(style).length ? style : undefined}
        {...props}
      />
    );
  },
);

SkeletonImage.propTypes = {
  width: PropTypes.string,
  height: PropTypes.string,
  aspectRatio: PropTypes.string,
  animated: PropTypes.bool,
  className: PropTypes.string,
};

export default SkeletonImage;
